<template>
    <section class="gallery-work-filter argus-glass-surface">
        <div class="gallery-work-filter__heading">
            <button type="button" class="gallery-work-filter__toggle" :aria-expanded="String(expanded)"
                :aria-controls="panelId" @click="expanded = !expanded">
                <i class="fas fa-filter" aria-hidden="true"></i>
                <span>Виды и группы работ</span>
                <span v-if="selectionCount" class="gallery-work-filter__count">{{ selectionCount }}</span>
                <i :class="expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
            </button>
            <button v-if="selectionCount" type="button" class="gallery-work-filter__reset" @click="clear">Сбросить</button>
        </div>
        <p v-if="selectionCount && !expanded" class="gallery-work-filter__summary" :title="selectionSummary">{{ selectionSummary }}</p>
        <div v-show="expanded" :id="panelId" class="gallery-work-filter__body">
            <div class="gallery-work-filter__fields">
                <div>
                    <label :for="panelId + '-groups'">Группы работ</label>
                    <v-select :input-id="panelId + '-groups'" :value="value.groupIds" :options="groupOptions"
                        :reduce="item => item.id" label="label" multiple :loading="loading" :disabled="loading"
                        placeholder="Любая группа" @input="select('groupIds', $event)">
                        <template #no-options>Группы не найдены</template>
                        <template #search="{attributes, events}">
                            <input class="vs__search" v-bind="attributes" aria-label="Группы работ" aria-labelledby="" v-on="events">
                        </template>
                    </v-select>
                </div>
                <div>
                    <label :for="panelId + '-works'">Виды работ</label>
                    <v-select :input-id="panelId + '-works'" :value="value.workIds" :options="workOptions"
                        :reduce="item => item.id" label="name" multiple :loading="loading" :disabled="loading"
                        placeholder="Любой вид работ" @input="select('workIds', $event)">
                        <template #no-options>Виды работ не найдены</template>
                        <template #option="option">
                            <span>{{ option.name }}</span>
                            <small class="gallery-work-filter__option-detail">{{ option.detail }}</small>
                        </template>
                        <template #selected-option="option">{{ option.selectedLabel }}</template>
                        <template #search="{attributes, events}">
                            <input class="vs__search" v-bind="attributes" aria-label="Виды работ" aria-labelledby="" v-on="events">
                        </template>
                    </v-select>
                </div>
            </div>
            <p class="gallery-work-filter__hint">Можно выбрать несколько: подойдёт любая из выбранных работ или групп.</p>
            <div v-if="loading" class="small text-muted" role="status">Загружаем виды работ…</div>
            <b-alert v-if="error" show variant="danger" class="mb-0" role="alert">
                {{ error }}
                <b-button size="sm" variant="outline-danger" :disabled="loading" @click="loadOptions">Повторить</b-button>
            </b-alert>
        </div>
        <p v-if="selectionCount" class="gallery-work-filter__notice" role="status">
            Отбор по работам смены. На фото и видео могут быть и другие работы.
        </p>
    </section>
</template>

<script>
import axios from 'axios';
import api from '@/utils/api';

export default {
    name: 'GalleryWorkFilter',
    inject: {galleryImageLoader: {default: null}},
    props: {
        value: {type: Object, required: true},
        processId: {type: Number, default: null},
        reportStatus: {type: String, default: 'all'},
        active: {type: Boolean, default: true}
    },
    data() {
        return {expanded: false, works: [], groups: [], loading: false, error: '', generation: 0, loaded: false};
    },
    computed: {
        panelId() { return 'gallery-work-filter-' + this._uid; },
        selectionCount() { return this.value.workIds.length + this.value.groupIds.length; },
        groupOptions() {
            const counts = new Map();
            this.groups.forEach(group => {
                const name = group.name.trim().toLocaleLowerCase('ru');
                counts.set(name, (counts.get(name) || 0) + 1);
            });
            return this.groups.map(group => ({...group, label: counts.get(group.name.trim().toLocaleLowerCase('ru')) > 1
                ? group.name + ' · №' + group.id : group.name}));
        },
        workOptions() {
            const groups = new Map(this.groupOptions.map(group => [group.id, group.label]));
            const counts = new Map(), groupCounts = new Map();
            this.works.forEach(work => {
                const name = work.name.trim().toLocaleLowerCase('ru'), key = work.group_id + ':' + name;
                counts.set(name, (counts.get(name) || 0) + 1);
                groupCounts.set(key, (groupCounts.get(key) || 0) + 1);
            });
            return this.works.map(work => {
                const name = work.name.trim().toLocaleLowerCase('ru');
                let detail = groups.get(work.group_id) || 'Без группы';
                if (groupCounts.get(work.group_id + ':' + name) > 1) detail += ' · №' + work.id;
                return {...work, detail, selectedLabel: counts.get(name) > 1 ? work.name + ' — ' + detail : work.name};
            });
        },
        selectionSummary() {
            return this.workOptions.filter(work => this.value.workIds.includes(work.id)).map(work => work.selectedLabel)
                .concat(this.groupOptions.filter(group => this.value.groupIds.includes(group.id)).map(group => 'Группа: ' + group.label))
                .join(' · ');
        }
    },
    watch: {
        expanded(value) {
            if (!value) this.cancelLoad();
            else if (this.active && !this.loaded) this.loadOptions();
        },
        active(value) {
            if (!value) this.cancelLoad();
            else if (this.expanded && !this.loaded) this.loadOptions();
        },
        reportStatus() {
            this.cancelLoad();
            this.loaded = false;
            this.error = '';
            if (this.active && this.expanded) this.loadOptions();
        },
        processId() {
            this.cancelLoad();
            this.works = [];
            this.groups = [];
            this.loaded = false;
            this.error = '';
            if (this.active && this.expanded) this.loadOptions();
        }
    },
    beforeDestroy() { this.cancelLoad(); },
    methods: {
        cancelLoad() {
            this.generation++;
            if (this._optionsRequest) this._optionsRequest.cancel();
            if (this._releasePriority) this._releasePriority();
            this._optionsRequest = null;
            this._releasePriority = null;
            this.loading = false;
        },
        select(key, ids) { this.$emit('input', {...this.value, [key]: ids || []}); },
        clear() { this.$emit('input', {workIds: [], groupIds: []}); },
        async loadOptions() {
            this.cancelLoad();
            const generation = this.generation;
            const request = axios.CancelToken.source();
            this._optionsRequest = request;
            const release = this.galleryImageLoader ? this.galleryImageLoader.pauseBackground() : () => {};
            this._releasePriority = release;
            this.loading = true;
            this.error = '';
            try {
                const {data} = await api.get('/api/photos/work-filter-options', {
                    cancelToken: request.token, localErrorHandling: true, ignoreModal: true, params: {
                    process_id: this.processId || undefined, report_status: this.reportStatus
                }});
                if (generation !== this.generation) return;
                // Retain selected labels across status changes, even when the new scope is empty.
                const keep = (next, old, ids) => next.concat(old.filter(item => ids.includes(item.id)
                    && !next.some(option => option.id === item.id)));
                this.works = keep(data.works, this.works, this.value.workIds);
                this.groups = keep(data.groups, this.groups, this.value.groupIds);
                this.loaded = true;
            } catch (err) {
                if (generation === this.generation) this.error = 'Не удалось загрузить виды и группы работ. Попробуйте ещё раз.';
            } finally {
                release();
                if (generation === this.generation) {
                    this.loading = false;
                    this._releasePriority = null;
                    this._optionsRequest = null;
                }
            }
        }
    }
};
</script>

<style scoped>
.gallery-work-filter { margin-bottom: 12px; padding: 8px 12px; border-radius: 10px; position: relative; z-index: 3;
    border: 1px solid var(--argus-glass-border); background: var(--argus-glass-fill); box-shadow: var(--argus-glass-shadow); }
.gallery-work-filter__heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.gallery-work-filter__toggle { display: flex; align-items: center; gap: 8px; border: 0; background: transparent; padding: 3px 0; min-height: 32px; color: #3f5366; text-align: left; font-size: .9rem; }
.gallery-work-filter__toggle .fa-chevron-down, .gallery-work-filter__toggle .fa-chevron-up { font-size: .65rem; }
.gallery-work-filter__count { background: #e7f2f7; border-radius: 12px; padding: 0 7px; font-weight: 600; }
.gallery-work-filter__reset { border: 0; background: transparent; color: #28627c; font-size: .82rem; padding: 5px; min-height: 32px; }
.gallery-work-filter__summary { color: #3f5366; font-size: .83rem; line-height: 1.45; margin: 4px 0 0;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere; }
.gallery-work-filter__option-detail { display: block; color: #637587; font-size: .75rem; line-height: 1.4; }
.gallery-work-filter >>> .vs__dropdown-option--highlight .gallery-work-filter__option-detail { color: inherit; }
.gallery-work-filter__body { padding-top: 12px; }
.gallery-work-filter__fields { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; }
.gallery-work-filter label { font-size: .82rem; font-weight: 600; color: #4e5b69; margin-bottom: 4px; }
.gallery-work-filter__hint, .gallery-work-filter__notice { color: #596877; font-size: .8rem; line-height: 1.5; margin: 8px 0 0; }
.gallery-work-filter >>> .vs__dropdown-toggle { background: #fff; }
.gallery-work-filter >>> .vs__selected { max-width: 100%; white-space: normal; overflow-wrap: anywhere; font-size: .85rem; }
.gallery-work-filter >>> .vs__selected-options { min-width: 0; }
.gallery-work-filter >>> .vs__dropdown-option { white-space: normal; overflow-wrap: anywhere; }
.gallery-work-filter >>> .vs__dropdown-menu { max-height: 260px; }
@media (max-width: 767.98px) {
    .gallery-work-filter__fields { grid-template-columns: minmax(0, 1fr); gap: 10px; }
    .gallery-work-filter >>> .vs__search { font-size: 16px; }
}
</style>
